// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

/**
 * 消息语义相等（单一事实源）。
 *
 * 用途：判断「两条 transcript 在语义上是否相同」，用于
 * 1. `chatStore.syncProjection` 的投影写入守卫；
 * 2. `chatStore.threadView` 的投影幂等守卫（避免渲染自喂循环）。
 *
 * 为什么必须下沉到 utils：
 * - store 层（`chatStore.threadView`）需要这个比较，但直接 import 页面层的
 *   `mainChatTranscriptReconciler`（785 行、还依赖其它页面工具）会形成
 *   store → 页面大模块的反向依赖；
 * - 该比较原本就写在 reconciler 里（`haveSameMessageShape`），这里是它的
 *   落地实现，reconciler 仅 re-export 以保持既有调用点不变。
 *
 * 为什么不能只比长度：
 * 2026-10-27 独立验收 P1 —— 早期投影守卫只比 `content` 与
 * `thinking_trace.steps.length`/`ui_blocks.length`，于是「同数量内的真实变化」
 * （同一 plot_card 参数 v0 10→20、同一步骤 running→completed、action_revision
 * 1→2）被误判为「没有变化」，store 缓存更新了而当前权威视图仍是旧值，两个
 * 读取面内容不一致。语义相等必须以**字段值**为准，不能以数量代替内容。
 *
 * 成本：先用 `===` 走便宜的原语比较，仅对对象/数组字段做序列化比较；调用点
 * （sync 投影、视图幂等）在稳态下已不再逐帧触发，因此不构成热路径负担。
 */

import type { Message } from '@/types';

/** 对象/数组字段的稳定比较（undefined 与缺失视为等价，null 与缺失不等价）。 */
function sameJsonValue(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (left === undefined || right === undefined) {
    // 缺失与 undefined 语义等价；缺失与 null 不等价（null 是有意的「清空」）。
    return left === undefined && right === undefined;
  }
  if (left === null || right === null) return false;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    // 不可序列化（含环/函数）：退化为引用比较，宁可判「不同」也不吞掉更新。
    return false;
  }
}

/** 单条消息的语义相等：覆盖消费者实际读取的正文、思考链、UI 载荷与交互标记。 */
export function haveSameMessageSemantics(left: Message, right: Message): boolean {
  if (left === right) return true;
  return (
    left.id === right.id &&
    left.role === right.role &&
    left.content === right.content &&
    left.timestamp === right.timestamp &&
    left.thread_id === right.thread_id &&
    // 思考链：内容/状态/步骤（不只数量），否则 running→completed 会被吞掉。
    left.thinking_duration_seconds === right.thinking_duration_seconds &&
    sameJsonValue(left.thinking_trace, right.thinking_trace) &&
    // UI 载荷：块内容（含 payload/参数）与可视化状态。
    sameJsonValue(left.ui_blocks, right.ui_blocks) &&
    sameJsonValue(left.visualization_state, right.visualization_state) &&
    // 交互标记与动作修订（同数量也必须比较）。
    left.ui_generation_mode === right.ui_generation_mode &&
    left.action_revision === right.action_revision &&
    left.interactive === right.interactive &&
    left.latest_snapshot === right.latest_snapshot &&
    left.can_accept_action_patch === right.can_accept_action_patch &&
    left.feedback === right.feedback &&
    // 其余渲染相关字段：保持「同数量/同形状但内容变化」也能被识别。
    sameJsonValue(left.sources, right.sources) &&
    left.agent_type === right.agent_type &&
    sameJsonValue(left.emotion_state, right.emotion_state) &&
    left.emotion_strategy === right.emotion_strategy &&
    sameJsonValue(left.capability_runtime, right.capability_runtime) &&
    left.discussion_artifact_id === right.discussion_artifact_id &&
    sameJsonValue(left.handoff_artifact, right.handoff_artifact) &&
    sameJsonValue(left.active_sessions, right.active_sessions) &&
    left.rag_status === right.rag_status &&
    left.web_search_status === right.web_search_status &&
    left.retrieval_mode === right.retrieval_mode &&
    left.active_agent === right.active_agent &&
    sameJsonValue(left.peer_handoff_marker, right.peer_handoff_marker)
  );
}

/**
 * 整段 transcript 的语义相等（顺序敏感：消息顺序变化即为不同）。
 * 数组长度相同不代表内容相同——逐元素按 {@link haveSameMessageSemantics} 比较。
 */
export function haveSameMessageShape(left: Message[], right: Message[]): boolean {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    const a = left[index];
    const b = right[index];
    if (!a || !b) return false;
    if (!haveSameMessageSemantics(a, b)) return false;
  }
  return true;
}
